import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import { Badge } from './badge';

/**
 * ClickableTag Component - Renders a clickable tag that toggles tag filter
 * @param {object} tag - Tag object with slug, name, etc.
 * @param {object} category - Optional category object with color info
 * @param {string} className - Additional CSS classes
 * @param {string} variant - Badge variant
 * @param {string} size - Tag size (xs, sm, md, lg)
 */
function ClickableTag({ 
  tag, 
  category, 
  className = '', 
  variant = 'secondary',
  size = 'sm',
  children
}) {
  const navigate = useNavigate();
  const { state, actions } = useApp();

  // Handle tag click - Toggle selection (add if not selected, remove if selected)
  const handleTagClick = (e) => {
    e.stopPropagation(); // Prevent parent click events
    
    if (tag?.slug) {
      // Navigate to main page first if not already there
      if (window.location.pathname !== '/') {
        navigate('/');
        // Use setTimeout to ensure navigation completes before applying filters
        setTimeout(() => {
          toggleTagFilter(tag.slug);
        }, 100);
      } else {
        toggleTagFilter(tag.slug);
      }
    }
  };

  const toggleTagFilter = (tagSlug) => {
    const currentTags = state.selectedTags || [];
    let newSelectedTags;
    
    if (currentTags.includes(tagSlug)) {
      // Tag is already selected - remove it (deselect)
      newSelectedTags = currentTags.filter(t => t !== tagSlug);
    } else {
      // Tag is not selected - add it
      newSelectedTags = [...currentTags, tagSlug];
    }
    
    // Update selected tags - the filtering is handled by SpotList useEffect
    actions.setSelectedTags(newSelectedTags);
    
    console.log(`Tag ${tagSlug} toggled. Selected tags:`, newSelectedTags);
  };

  // Tag display text
  const tagText = children || `#${tag?.name || tag?.slug || 'tag'}`;

  // Check if this tag is currently selected
  const isSelected = state.selectedTags?.includes(tag?.slug);

  // Size classes
  const sizeClasses = {
    xs: 'text-xs px-1.5 py-0.5',
    sm: 'text-xs px-2 py-1',
    md: 'text-sm px-2.5 py-1.5',
    lg: 'text-base px-3 py-2'
  };

  // Dynamic styling based on selection state and category
  const getTagStyle = () => {
    if (isSelected) {
      // Selected state - make it prominent with category color
      return category ? {
        backgroundColor: category.color,
        color: 'white',
        border: `2px solid ${category.color}`,
        fontWeight: '600',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      } : {
        backgroundColor: '#3b82f6', // blue-500
        color: 'white',
        border: '2px solid #3b82f6',
        fontWeight: '600',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      };
    } else {
      // Unselected state - use category colors with transparency
      return category ? {
        backgroundColor: `${category.color}15`,
        color: category.color,
        border: `1px solid ${category.color}40`
      } : {
        backgroundColor: '#f3f4f6',
        color: '#4b5563',
        border: '1px solid #e5e7eb'
      };
    }
  };

  return (
    <Badge
      variant={variant}
      className={`cursor-pointer transition-all duration-200 ${sizeClasses[size]} ${className} ${
        isSelected ? 'ring-2 ring-offset-1 ring-opacity-50 scale-105' : 'hover:scale-102 hover:shadow-sm'
      }`}
      style={{
        ...getTagStyle(),
        ringColor: isSelected && category ? category.color : '#3b82f6'
      }}
      onClick={handleTagClick}
      title={isSelected ? `Retirer le filtre: ${tag?.name}` : `Filtrer par: ${tag?.name}`}
    >
      {tagText}
      {isSelected && <span className="ml-1">✓</span>}
    </Badge>
  );
}

export default ClickableTag;